import { createSlice, createEntityAdapter } from "@reduxjs/toolkit"

import type { RootState } from "redux/store"
import {
  handleFetchFulfilled,
  handleFetchPending,
  handleFetchRejected,
  handleCollectionFetchPending,
  handleCollectionFetchRejected,
  handleCollectionFetchFulfilled,
} from "redux/helpers/fetching"
import { fetchDebts, fetchDebt } from "redux/features/debt/debtThunk"
import { fetchDebtContractAttachment } from "redux/features/debt/debtContractDownloadThunk"
import { DebtType } from "types"

const debtAdapter = createEntityAdapter({
  selectId: (debt: DebtType) => debt.id,
})

export const initialState = debtAdapter.getInitialState({
  unsolved: [],
  draft_sent_to_self_service: [],
  draft_payment_schedule: [],
  payment_schedule: [],
  overdue: [],
  court_preparing: [],
  court: [],
  bailiff: [],
  paid: [],
  ended: [],
  meta: {},
  fetchStatus: {
    isFetching: false,
    isSuccess: false,
    isFailed: false,
  },
  fetchCollectionStatus: {
    isFetching: false,
    isSuccess: false,
    isFailed: false,
  },
})

export const debtSlice = createSlice({
  name: "debt",
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchDebt.pending, (state) => handleFetchPending(state))
      .addCase(fetchDebt.rejected, (state) => handleFetchRejected(state))
      .addCase(fetchDebt.fulfilled, (state, { payload }) => {
        debtAdapter.setOne(state, payload)
        handleFetchFulfilled(state)
      })
      .addCase(fetchDebts.pending, (state) =>
        handleCollectionFetchPending(state),
      )
      .addCase(fetchDebts.rejected, (state) =>
        handleCollectionFetchRejected(state),
      )
      .addCase(fetchDebts.fulfilled, (state, { payload }) => {
        const {
          unsolved,
          draft_sent_to_self_service,
          draft_payment_schedule,
          payment_schedule,
          overdue,
          court_preparing,
          court,
          bailiff,
          paid,
          ended,
          meta,
        } = payload

        state.unsolved = unsolved ? unsolved : []
        state.draft_sent_to_self_service = draft_sent_to_self_service
          ? draft_sent_to_self_service
          : []
        state.draft_payment_schedule = draft_payment_schedule
          ? draft_payment_schedule
          : []
        state.payment_schedule = payment_schedule ? payment_schedule : []
        state.overdue = overdue ? overdue : []
        state.court_preparing = court_preparing ? court_preparing : []
        state.court = court ? court : []
        state.bailiff = bailiff ? bailiff : []
        state.paid = paid ? paid : []
        state.ended = ended ? ended : []

        state.meta = meta ? meta : {}

        const collection = [
          state.unsolved,
          state.draft_sent_to_self_service,
          state.draft_payment_schedule,
          state.payment_schedule,
          state.overdue,
          state.court_preparing,
          state.court,
          state.bailiff,
          state.paid,
          state.ended,
        ].flat()

        debtAdapter.setAll(state, collection)
        handleCollectionFetchFulfilled(state)
      })
      .addCase(fetchDebtContractAttachment.pending, (state) =>
        handleFetchPending(state),
      )
      .addCase(fetchDebtContractAttachment.rejected, (state) =>
        handleFetchRejected(state),
      )
      .addCase(fetchDebtContractAttachment.fulfilled, (state) => {
        handleFetchFulfilled(state)
      })
  },
})

export const {
  selectById: selectDebtById,
  selectAll,
  selectTotal,
} = debtAdapter.getSelectors((state: RootState) => state.features.debt)
export const selectUnsolvedDebts = (state: RootState) =>
  state.features.debt.unsolved
export const selectDraftSentToSelfServiceDebts = (state: RootState) =>
  state.features.debt.draft_sent_to_self_service
export const selectDraftPaymentScheduleDebts = (state: RootState) =>
  state.features.debt.draft_payment_schedule
export const selectPaymentScheduleDebts = (state: RootState) =>
  state.features.debt.payment_schedule
export const selectOverdueDebts = (state: RootState) =>
  state.features.debt.overdue
export const selectCourtPreparingDebts = (state: RootState) =>
  state.features.debt.court_preparing
export const selectCourtDebts = (state: RootState) => state.features.debt.court
export const selectBailiffDebts = (state: RootState) =>
  state.features.debt.bailiff
export const selectPaidDebts = (state: RootState) => state.features.debt.paid
export const selectEndedDebts = (state: RootState) => state.features.debt.ended
export const selectDebtsMeta = (state: RootState) => state.features.debt.meta

export const selectFetchStatus = (state: RootState) =>
  state.features.debt.fetchStatus
export const selectFetchCollectionStatus = (state: RootState) =>
  state.features.debt.fetchCollectionStatus

export default debtSlice.reducer
